/**
 * fontExperiment.ts - Muna Arabic Font Experiment Toggle
 *
 * Staging experiment for the "Arabic chat text is faint and too small" report.
 * Puts the .exp-muna* classes that App.css keys off onto <html>.
 *
 * Modes (?font=…):
 *   muna12      Muna, 12px, size-adjust 160%  (default)
 *   muna14      Muna, 14px, size-adjust 160%
 *   muna14-raw  Muna, 14px, no size-adjust    (looks ~45% smaller)
 *   muna12-raw  Muna, 12px, no size-adjust
 *   off         current droid_arabic_kufi
 *
 * The choice sticks in localStorage, so testers set it once per device.
 */

const STORAGE_KEY = "ns.fontExperiment";

const MODES = ["muna14", "muna12", "muna14-raw", "muna12-raw", "off"] as const;
export type FontExperimentMode = (typeof MODES)[number];

const isMode = (value: string | null): value is FontExperimentMode =>
    !!value && (MODES as readonly string[]).includes(value);

/**
 * Reads ?font= from the URL. The app runs under a HashRouter, so the param can
 * sit either before the hash (…/sessions/?font=muna12#/aichat/…) or inside it
 * (…/sessions/#/aichat/1/new?font=muna12) — accept both.
 */
function modeFromUrl(): FontExperimentMode | null {
    const search = new URLSearchParams(window.location.search).get("font");
    if (isMode(search)) return search;

    const hashQuery = window.location.hash.split("?")[1];
    const hash = hashQuery ? new URLSearchParams(hashQuery).get("font") : null;
    return isMode(hash) ? hash : null;
}

/** Muna at 12 is the default everywhere; ?font=off still opts out. */
function defaultMode(): FontExperimentMode {
    return "muna12";
}

export function applyFontExperiment() {
    const urlMode = modeFromUrl();
    if (urlMode) {
        try {
            window.localStorage.setItem(STORAGE_KEY, urlMode);
        } catch {
            // Private mode / blocked storage — the URL param still applies for this load.
        }
    }

    let stored: string | null = null;
    try {
        stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
        stored = null;
    }

    const mode = urlMode ?? (isMode(stored) ? stored : defaultMode());

    const root = document.documentElement;
    root.classList.remove("exp-muna", "exp-muna-raw", "exp-muna-12", "exp-muna-14");
    if (mode === "off") return;

    root.classList.add("exp-muna");
    root.classList.add(mode.startsWith("muna12") ? "exp-muna-12" : "exp-muna-14");
    if (mode.endsWith("-raw")) root.classList.add("exp-muna-raw");
}
